import { useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useTranslation } from 'react-i18next';
import { formatCentsForDisplay } from '@/utils/money';
import {
  FILE_CLASSIFICATION_KINDS,
  FILE_CLASSIFICATION_UNCERTAIN_BELOW,
  isFileClassificationKind,
  type FileClassification,
  type FileClassificationKind,
  type FileClassificationLine,
} from './fileClassification';

const VISIBLE_LINES = 5;

const KIND_ICON: Record<FileClassificationKind, typeof FactCheckOutlinedIcon> = {
  inspection_certificate: FactCheckOutlinedIcon,
  parts_list: ListAltIcon,
  vehicle_photo: DirectionsCarOutlinedIcon,
  engine_photo: SettingsOutlinedIcon,
  damage_photo: ReportProblemOutlinedIcon,
  fault_codes: ErrorOutlineIcon,
  registration_document: BadgeOutlinedIcon,
  invoice_or_receipt: ReceiptLongOutlinedIcon,
  other: InsertDriveFileOutlinedIcon,
};

interface FileClassificationCardProps {
  classification: FileClassification;
  /** Transcript name of the attachment, when the user turn is still loaded. */
  fileName?: string;
  disabled?: boolean;
  /** Sends the correction as the next user turn. The card only writes the sentence. */
  onSend: (message: string) => void;
}

function formatQuantity(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 4 }).format(value);
}

function formatIsoDate(iso: string, language: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

function lineQuantity(
  line: FileClassificationLine,
  language: string,
  quantityWithUnit: (quantity: string, unit: string) => string
): string {
  if (line.quantity === undefined) return '';
  const quantity = formatQuantity(line.quantity, language);
  return line.unit ? quantityWithUnit(quantity, line.unit) : quantity;
}

export function FileClassificationCard({
  classification,
  fileName,
  disabled = false,
  onSend,
}: FileClassificationCardProps) {
  const { t, i18n } = useTranslation('assistant');
  const [targetOpen, setTargetOpen] = useState(false);
  const [targetDraft, setTargetDraft] = useState('');

  const kindLabel = t(`fileScan.kind.${classification.kind}`);
  const fileLabel = fileName?.trim() ? fileName.trim() : t('fileScan.thisFile');
  const Icon = KIND_ICON[classification.kind];
  const uncertain = classification.confidence < FILE_CLASSIFICATION_UNCERTAIN_BELOW;
  const kindLabelId = `file-scan-kind-${classification.attachmentId}`;
  const targetUnknown = classification.target.label == null;
  const showTargetField = targetOpen || targetUnknown;
  const lines = classification.fields.lines ?? [];
  const visibleLines = lines.slice(0, VISIBLE_LINES);
  const faultCodes = classification.fields.faultCodes ?? [];
  const observations = classification.fields.observations ?? [];
  const { registrationNumber, vin, date, inspectionResult } = classification.fields;
  const hasDetails = Boolean(registrationNumber || vin || date || inspectionResult);

  const sendKind = (next: string) => {
    if (!isFileClassificationKind(next) || next === classification.kind) return;
    onSend(
      t('fileScan.correction.kind', { fileName: fileLabel, kind: t(`fileScan.kind.${next}`) })
    );
  };

  const submitTarget = (event: FormEvent) => {
    event.preventDefault();
    const target = targetDraft.trim();
    if (!target) {
      setTargetOpen(true);
      return;
    }
    onSend(t('fileScan.correction.target', { fileName: fileLabel, target }));
  };

  return (
    <Box sx={{ mb: 1.5, ml: 4.5 }} data-testid="file-classification-card">
      <Card
        component="section"
        variant="outlined"
        aria-label={t('fileScan.cardLabel', { kind: kindLabel, fileName: fileLabel })}
        sx={{ maxWidth: '85%' }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon fontSize="small" color="action" aria-hidden={true} />
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {kindLabel}
            </Typography>
            {uncertain && (
              <Chip
                size="small"
                color="warning"
                label={t('fileScan.uncertain')}
                data-testid="file-classification-uncertain"
              />
            )}
          </Box>
          {fileName?.trim() ? (
            <Typography variant="caption" color="text.secondary" component="p">
              {fileName.trim()}
            </Typography>
          ) : null}
          {classification.summary.length > 0 && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {classification.summary}
            </Typography>
          )}

          {hasDetails && (
            <Box
              component="dl"
              sx={{
                m: 0,
                mt: 1,
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 1,
                rowGap: 0.25,
              }}
            >
              {registrationNumber && (
                <>
                  <Typography component="dt" variant="caption" color="text.secondary">
                    {t('fileScan.field.registrationNumber')}
                  </Typography>
                  <Typography component="dd" variant="caption" sx={{ m: 0 }}>
                    {registrationNumber}
                  </Typography>
                </>
              )}
              {vin && (
                <>
                  <Typography component="dt" variant="caption" color="text.secondary">
                    {t('fileScan.field.vin')}
                  </Typography>
                  <Typography component="dd" variant="caption" sx={{ m: 0 }}>
                    {vin}
                  </Typography>
                </>
              )}
              {date && (
                <>
                  <Typography component="dt" variant="caption" color="text.secondary">
                    {t('fileScan.field.date')}
                  </Typography>
                  <Typography component="dd" variant="caption" sx={{ m: 0 }}>
                    {formatIsoDate(date, i18n.language)}
                  </Typography>
                </>
              )}
              {inspectionResult && (
                <>
                  <Typography component="dt" variant="caption" color="text.secondary">
                    {t('fileScan.field.inspectionResult')}
                  </Typography>
                  <Typography component="dd" variant="caption" sx={{ m: 0 }}>
                    {t(`fileScan.inspectionResult.${inspectionResult}`)}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {visibleLines.length > 0 && (
            <Box sx={{ mt: 1, overflowX: 'auto' }}>
              <Table size="small" aria-label={t('fileScan.lines')}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.25, px: 0.5 }}>{t('fileScan.line.name')}</TableCell>
                    <TableCell align="right" sx={{ py: 0.25, px: 0.5 }}>
                      {t('fileScan.line.quantity')}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.25, px: 0.5 }}>
                      {t('fileScan.line.price')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleLines.map((line, index) => (
                    <TableRow
                      key={`${line.partNumber ?? line.name}-${index}`}
                      sx={{ '&:last-child td': { border: 0 } }}
                    >
                      <TableCell sx={{ py: 0.25, px: 0.5 }}>
                        <Typography variant="caption" component="p">
                          {line.name}
                        </Typography>
                        {line.partNumber && (
                          <Typography variant="caption" color="text.secondary" component="p">
                            {t('fileScan.line.partNumberLine', { partNumber: line.partNumber })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25, px: 0.5 }}>
                        <Typography variant="caption">
                          {lineQuantity(line, i18n.language, (quantity, unit) =>
                            t('fileScan.line.quantityWithUnit', { quantity, unit })
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.25, px: 0.5 }}>
                        <Typography variant="caption">
                          {line.unitPriceCents === undefined
                            ? ''
                            : formatCentsForDisplay(line.unitPriceCents)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {lines.length > VISIBLE_LINES && (
                <Typography variant="caption" color="text.secondary" component="p">
                  {t('fileScan.moreLines', { count: lines.length })}
                </Typography>
              )}
            </Box>
          )}

          {faultCodes.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" component="p">
                {t('fileScan.faultCodes')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {faultCodes.map((item, index) => (
                  <Typography component="li" variant="caption" key={`${item.code}-${index}`}>
                    {item.description
                      ? t('fileScan.faultCodeLine', {
                          code: item.code,
                          description: item.description,
                        })
                      : item.code}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {observations.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" component="p">
                {t('fileScan.observationsEstimate')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {observations.map((observation, index) => (
                  <Typography
                    component="li"
                    variant="caption"
                    key={`${index}-${observation.slice(0, 24)}`}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {observation}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="body2" sx={{ mt: 1 }}>
            {classification.target.label
              ? t('fileScan.targetLine', { target: classification.target.label })
              : t('fileScan.targetUnknown')}
          </Typography>

          {classification.missing.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="p">
              {t('fileScan.missingLead')}{' '}
              {classification.missing.map((item) => t(`fileScan.missing.${item}`)).join(', ')}
            </Typography>
          )}

          {classification.suggestedActions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" component="p">
                {t('fileScan.suggestedActions')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {classification.suggestedActions.map((action) => (
                  <Chip
                    key={action}
                    size="small"
                    variant="outlined"
                    label={t(`fileScan.action.${action}`)}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
              <InputLabel id={kindLabelId}>{t('fileScan.correctType')}</InputLabel>
              <Select
                labelId={kindLabelId}
                label={t('fileScan.correctType')}
                value={classification.kind}
                onChange={(event) => sendKind(event.target.value)}
              >
                {FILE_CLASSIFICATION_KINDS.map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {t(`fileScan.kind.${kind}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box
              component="form"
              onSubmit={submitTarget}
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
            >
              {showTargetField && (
                <TextField
                  size="small"
                  label={t('fileScan.targetField')}
                  value={targetDraft}
                  disabled={disabled}
                  onChange={(event) => setTargetDraft(event.target.value)}
                  autoComplete="off"
                />
              )}
              <Button type="submit" size="small" disabled={disabled}>
                {t('fileScan.changeTarget')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
